import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { Badge, Box, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip } from "@mui/material";
import React from "react";
import IconComponent from "../../../common/components/IconComponent";
import { usePersistedSetting, useQueryParamState } from "../../../common/hooks";
import { useDesignerMode } from "../../Contexts/DesignerModeContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { DrawerMenuDirection, DrawerMenuItemModel, DrawerMenuModel } from "../../Models/DrawerMenuModel";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const DRAWER_MENU_ITEMS_PATH_SEGMENT = "items";
const DRAWER_MENU_ITEM_TYPE = "DrawerMenuItemModel";

function startsWithPath(path: string[] | null | undefined, prefix: string[]) {
  if (!path || path.length < prefix.length) {
    return false;
  }

  return prefix.every((segment, index) => path[index] === segment);
}

type DrawerMenuItemButtonProps = {
  drawerMenuId: string;
  item: DrawerMenuItemModel;
  index: number;
  selected: boolean;
  open: boolean;
  tooltipPlacement: "left" | "right";
  componentPath: string[];
  context: IComponentProps<DrawerMenuModel>["context"];
  onSelect: () => void;
};

const DrawerMenuItemButton: React.FC<DrawerMenuItemButtonProps> = ({ drawerMenuId, item, index, selected, open, tooltipPlacement, componentPath, context, onSelect }) => {
  const [hidden] = useObservedExpression<boolean>(item.Hidden, context, "DrawerMenuItem.Hidden", (value) => value === true);
  const [disabled] = useObservedExpression<boolean>(item.Disabled, context, "DrawerMenuItem.Disabled", (value) => value === true);
  const [badge] = useObservedExpression<React.ReactNode>(item.Badge, context, "DrawerMenuItem.Badge");
  const selectionProps = useComponentSelection({
    component: {
      Id: `${drawerMenuId}-item-${index}`,
      Type: DRAWER_MENU_ITEM_TYPE,
      Layout: {},
    },
    componentPath: [...componentPath, DRAWER_MENU_ITEMS_PATH_SEGMENT, String(index)],
    isContainer: false,
  });

  if (hidden) {
    return null;
  }

  const icon = item.IconName ? <IconComponent iconName={item.IconName} /> : <MenuIcon fontSize="small" />;
  const button = (
    <ListItemButton
      selected={selected}
      disabled={disabled}
      onClick={onSelect}
      sx={{ minHeight: 44, justifyContent: open ? "initial" : "center", px: open ? 1.5 : 0.5 }}
    >
      <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 0, justifyContent: "center" }}>
        <Badge badgeContent={badge} color="primary" overlap="circular">
          {icon}
        </Badge>
      </ListItemIcon>
      {open && <ListItemText primary={item.Label || `Item ${index + 1}`} secondary={item.Description} />}
    </ListItemButton>
  );
  const wrappedButton = (
    <Box {...selectionProps} sx={{ display: "block" }}>
      {button}
    </Box>
  );

  if (open) {
    return wrappedButton;
  }

  return (
    <Tooltip title={item.Label || `Item ${index + 1}`} placement={tooltipPlacement}>
      {wrappedButton}
    </Tooltip>
  );
};

const DrawerMenuComponent: React.FC<IComponentProps<DrawerMenuModel>> = ({ component, context, componentPath = [] }) => {
  const designerMode = useDesignerMode();
  const componentLayoutSx = getComponentLayout(component);
  const collapsible = component.Collapsible ?? true;
  const persistedKey = component.PersistedStateKey ?? `drawerMenu.${component.Id}`;
  const [persistedOpen, setPersistedOpen] = usePersistedSetting<boolean>(`${persistedKey}.open`, component.DefaultOpen ?? false);
  const [persistedSelectedIndex, setPersistedSelectedIndex] = usePersistedSetting<number | null>(`${persistedKey}.selectedIndex`, null);
  const [localSelectedIndex, setLocalSelectedIndex] = React.useState<number | null>(null);
  const [querySelectedIndex, setQuerySelectedIndex] = useQueryParamState(component.ItemQueryKey);

  const open = collapsible ? persistedOpen : true;
  const itemCount = component.Items?.length ?? 0;
  const parsedQuerySelectedIndex = querySelectedIndex !== null ? Number.parseInt(querySelectedIndex, 10) : null;
  const rawSelectedIndex =
    parsedQuerySelectedIndex !== null && Number.isInteger(parsedQuerySelectedIndex)
      ? parsedQuerySelectedIndex
      : component.PersistSelection === false
        ? localSelectedIndex
        : persistedSelectedIndex;
  const designSelectedIndex = React.useMemo(() => {
    const selectedPath = designerMode?.selectedPath;
    if (!selectedPath) {
      return null;
    }

    const virtualItemPath = [...componentPath, DRAWER_MENU_ITEMS_PATH_SEGMENT];
    if (startsWithPath(selectedPath, virtualItemPath)) {
      const itemIndex = Number.parseInt(selectedPath[virtualItemPath.length], 10);
      return Number.isInteger(itemIndex) && itemIndex >= 0 && itemIndex < itemCount ? itemIndex : null;
    }

    if (startsWithPath(selectedPath, componentPath)) {
      const selectedChildId = selectedPath[componentPath.length];
      const childIndex = component.Children?.findIndex((child) => child.Id === selectedChildId) ?? -1;
      return childIndex >= 0 && childIndex < itemCount ? childIndex : null;
    }

    return null;
  }, [component.Children, componentPath, designerMode?.selectedPath, itemCount]);
  const activeSelectedIndex = designSelectedIndex ?? rawSelectedIndex;
  const selectedIndex = activeSelectedIndex !== null && activeSelectedIndex >= 0 && activeSelectedIndex < itemCount ? activeSelectedIndex : null;
  const hasSelectedChild = selectedIndex !== null && !!component.Children?.[selectedIndex];
  const collapsedWidth = component.CollapsedWidth ?? 56;
  const expandedWidth = component.ExpandedWidth ?? 320;
  const hasContentWidth = component.ContentWidth !== undefined && component.ContentWidth !== null;
  const direction = component.Direction ?? component.OpeningDirection ?? DrawerMenuDirection.Left;
  const opensLeft = direction === DrawerMenuDirection.Left;
  const overlayContent = component.OverlayContent === true;

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true,
    isEmpty: !component.Children || component.Children.length === 0,
  });

  const railWidth = open ? expandedWidth : collapsedWidth;
  const railLayoutWidth = overlayContent && open ? collapsedWidth : railWidth;
  const showContent = hasSelectedChild;
  const contentWidth = showContent && hasContentWidth ? component.ContentWidth : undefined;
  const drawerWidth = showContent ? (contentWidth !== undefined ? railLayoutWidth + contentWidth : "max-content") : railLayoutWidth;
  const clearSelection = () => {
    if (component.PersistSelection === false) {
      setLocalSelectedIndex(null);
    } else {
      setPersistedSelectedIndex(null);
    }

    setQuerySelectedIndex(null);
  };
  const toggleIcon = open
    ? opensLeft
      ? <ChevronRightIcon fontSize="small" />
      : <ChevronLeftIcon fontSize="small" />
    : opensLeft
      ? <ChevronLeftIcon fontSize="small" />
      : <ChevronRightIcon fontSize="small" />;
  const headerContent = (
    <Box
      sx={{
        display: showContent ? "flex" : "none",
        width: contentWidth,
        flex: hasContentWidth ? "0 0 auto" : 1,
        minWidth: 0,
        justifyContent: "flex-end",
        p: 0.5,
        boxSizing: "border-box",
      }}
    >
      {hasSelectedChild && (
        <IconButton size="small" onClick={clearSelection} aria-label="close drawer content">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
  const headerRail = (
    <Box sx={{ display: "flex", width: railLayoutWidth, flexShrink: 0, justifyContent: "center", p: 0.5, boxSizing: "border-box" }}>
      {collapsible && (
        <IconButton size="small" onClick={() => setPersistedOpen((current) => !current)} aria-label={open ? "collapse drawer menu" : "expand drawer menu"}>
          {toggleIcon}
        </IconButton>
      )}
    </Box>
  );
  const header = (
    <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
      {opensLeft ? headerContent : headerRail}
      {opensLeft ? headerRail : headerContent}
    </Box>
  );
  const rail = (
    <Box sx={{ width: railLayoutWidth, flexShrink: 0, position: "relative", overflow: "visible", zIndex: overlayContent && open ? 2 : undefined }}>
      <Box
        sx={{
          width: railWidth,
          height: "100%",
          position: overlayContent && open ? "absolute" : "relative",
          top: 0,
          left: overlayContent && open && !opensLeft ? 0 : undefined,
          right: overlayContent && open && opensLeft ? 0 : undefined,
          borderLeft: showContent && opensLeft ? 1 : undefined,
          borderRight: showContent && !opensLeft ? 1 : undefined,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: overlayContent && open ? 3 : undefined,
          transition: (theme) => theme.transitions.create("width"),
        }}
      >
        <List dense sx={{ p: 0.5 }}>
          {(component.Items ?? []).map((item, index) => (
            <DrawerMenuItemButton
              key={index}
              drawerMenuId={component.Id}
              item={item}
              index={index}
              selected={selectedIndex === index}
              open={open}
              tooltipPlacement={opensLeft ? "left" : "right"}
              componentPath={componentPath}
              context={context}
              onSelect={() => handleSelect(index)}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
  const content = (
    <Box
      sx={{
        display: showContent ? "block" : "none",
        width: contentWidth ?? "max-content",
        maxWidth: contentWidth,
        flex: "0 0 auto",
        minWidth: 0,
        overflow: "auto",
        p: 0,
      }}
    >
      {component.Children?.map((child, index) => (
        <Box key={child.Id} sx={{ display: selectedIndex === index ? "block" : "none" }}>
          <ComponentFactory parent={component} component={child} context={context} componentPath={[...componentPath, child.Id]} />
        </Box>
      ))}
    </Box>
  );

  const handleSelect = (index: number) => {
    const nextSelectedIndex = selectedIndex === index ? null : index;

    if (component.PersistSelection === false) {
      setLocalSelectedIndex(nextSelectedIndex);
    } else {
      setPersistedSelectedIndex(nextSelectedIndex);
    }

    setQuerySelectedIndex(nextSelectedIndex);
  };
  const surfaceSx = {
    ...componentLayoutSx,
    display: "flex",
    flexDirection: "column",
    width: componentLayoutSx.width ?? drawerWidth,
    minHeight: componentLayoutSx.minHeight ?? 360,
    overflow: "hidden",
  } as const;
  const drawerMenuContent = (
    <>
      {header}
      <Box sx={{ display: "flex", flex: 1, width: "100%", minHeight: 0, minWidth: 0 }}>
        {opensLeft && content}
        {rail}
        {!opensLeft && content}
      </Box>
    </>
  );

  if (component.UsePaper === false) {
    return (
      <Box sx={surfaceSx} {...selectionProps}>
        {drawerMenuContent}
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={surfaceSx} {...selectionProps}>
      {drawerMenuContent}
    </Paper>
  );
};

export default DrawerMenuComponent;
